import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from "firebase/firestore";
import { Plus, Search, Trash2, CreditCard, History, ArrowLeft, MoreVertical, Edit2 } from 'lucide-react';
import { AccountsInvoiceModal, PaymentModal } from './AccountsModals.js';
import { Link } from 'react-router-dom';
import Spinner from './Spinner.js';
import useClickOutside from './hooks/useClickOutside.js';

const AccountsInvoices = ({ db }) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    
    const [showInvModal, setShowInvModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    
    const [showPayModal, setShowPayModal] = useState(false);
    const [invoiceForPayment, setInvoiceForPayment] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "accounts_invoices"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [db]);

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = inv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             inv.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusStyle = (invoice) => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const dueDate = new Date(invoice.dueDate);
        dueDate.setHours(0,0,0,0);

        const isOverdue = invoice.balance > 0 && dueDate < today;

        if (isOverdue) return { bg: '#fee2e2', text: '#991b1b', label: 'Overdue' };

        switch(invoice.status) {
            case 'paid': return { bg: '#dcfce7', text: '#166534', label: 'Paid' };
            case 'partial': return { bg: '#fef9c3', text: '#854d0e', label: 'Partial' };
            case 'pending': return { bg: '#eff6ff', text: '#2563eb', label: 'Pending' }; // Light blue for pending
            default: return { bg: '#f1f5f9', text: '#475569', label: invoice.status };
        }
    };

    if (loading) return <Spinner />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <Link to="/accounts" style={{ color: '#64748b' }}><ArrowLeft size={20}/></Link>
                    <h2 style={{ margin: 0 }}>Manage Invoices</h2>
                </div>
                {/* New Invoice button moved to Layout.js */}
            </div>

            {/* Filters */}
            <div style={{ 
                display: 'flex', gap: '15px', marginBottom: '25px', 
                backgroundColor: 'white', padding: '15px', borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <input 
                        placeholder="Search customer or invoice number..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ 
                            width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', 
                            border: '1px solid #e2e8f0', outline: 'none' 
                        }}
                    />
                </div>
                <select 
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}
                >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="partial">Partially Paid</option>
                    <option value="paid">Fully Paid</option>
                    <option value="overdue">Overdue</option>
                </select>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={thStyle}>Invoice No.</th>
                            <th style={thStyle}>Customer</th>
                            <th style={thStyle}>Total</th>
                            <th style={thStyle}>Paid</th>
                            <th style={thStyle}>Balance</th>
                            <th style={thStyle}>Issue Date</th>
                            <th style={thStyle}>Due Date</th>
                            <th style={thStyle}>Status</th>
                            <th style={{...thStyle, textAlign: 'center'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInvoices.map(inv => {
                            const status = getStatusStyle(inv);
                            const isOverdue = inv.balance > 0 && new Date(inv.dueDate) < new Date();
                            const rowBg = isOverdue ? '#fff8f8' : 'white'; // Light red background for overdue

                            return (
                                <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: rowBg }}>
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: 'bold' }}>{inv.invoiceNo}</div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>{inv.salesPerson}</div>
                                    </td>
                                    <td style={tdStyle}>{inv.customerName}</td>
                                    <td style={tdStyle}>₹{inv.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={{...tdStyle, color: '#059669'}}>₹{inv.amountPaid.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={{...tdStyle, fontWeight: 'bold', color: inv.balance > 0 ? '#dc2626' : '#059669'}}>₹{inv.balance.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                    <td style={tdStyle}>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                    <td style={tdStyle}>{new Date(inv.dueDate).toLocaleDateString()}</td>
                                    <td style={tdStyle}>
                                        <span style={{ 
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', height: 'fit-content',
                                            backgroundColor: status.bg, color: status.text
                                        }}>
                                            {status.label}
                                        </span>
                                    </td>
                                    <td style={{...tdStyle, textAlign: 'center'}}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            {inv.balance > 0 && (
                                                <button 
                                                    onClick={() => { setInvoiceForPayment(inv); setShowPayModal(true); }}
                                                    style={{ 
                                                        backgroundColor: '#2563eb', color: 'white', border: 'none', 
                                                        padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                                                        display: 'flex', alignItems: 'center', gap: '4px'
                                                    }}
                                                    title="Record Payment"
                                                >
                                                    <CreditCard size={12} /> Pay
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => { setSelectedInvoice(inv); setShowInvModal(true); }}
                                                style={{ 
                                                    padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', 
                                                    backgroundColor: 'white', cursor: 'pointer' 
                                                }}
                                                title="Edit Invoice"
                                            >
                                                <Edit2 size={12} color="#64748b" />
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    if(window.confirm("Are you sure you want to delete this invoice?")) {
                                                        await deleteDoc(doc(db, "accounts_invoices", inv.id));
                                                        fetchData();
                                                    }
                                                }}
                                                style={{ 
                                                    padding: '6px 8px', borderRadius: '6px', border: '1px solid #fee2e2', 
                                                    backgroundColor: 'white', cursor: 'pointer' 
                                                }}
                                                title="Delete Invoice"
                                            >
                                                <Trash2 size={12} color="#ef4444" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredInvoices.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No invoices found matching your filters.</div>
                )}
            </div>

            <AccountsInvoiceModal 
                show={showInvModal} 
                onClose={() => setShowInvModal(false)} 
                onSave={fetchData} 
                db={db} 
                invoiceData={selectedInvoice}
            />

            <PaymentModal 
                show={showPayModal} 
                onClose={() => setShowPayModal(false)} 
                onSave={fetchData} 
                db={db} 
                invoice={invoiceForPayment}
            />
        </div>
    );
};

// --- STYLES ---
const thStyle = { padding: '12px 15px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', verticalAlign: 'middle' };

export default AccountsInvoices;
